import {
	Chip,
	FormControl,
	IconButton,
	InputLabel,
	MenuItem,
	Select,
	Stack,
	TextField,
} from "@mui/material";
import { Delete } from "@mui/icons-material";
import { ProjectTimeEntryStatus, type ProjectTimeEntry } from "@repo/server-api";
import { TimeRangeInput } from "@repo/shared/components";
import React from "react";
import { useTranslation } from "react-i18next";

import type { TimeEntryRowProps, NewTimeEntry } from "./types";
import { stringToTimeRangeValue, timeRangeValueToString } from "./utils";
import { LogsCell } from "./LogsCell";
import { HistoryCell } from "./HistoryCell";

export const TimeEntryRow = ({
	entry,
	projectPositions,
	timeTrackingResolution,
	onChange,
	onDelete,
	disabled,
}: TimeEntryRowProps) => {
	const { t } = useTranslation();

	// Konvertiere start/end zu TimeRangeValue
	const timeRangeValue = stringToTimeRangeValue(entry.start, entry.end);

	// Status Farbe
	const getStatusStyle = (status: ProjectTimeEntryStatus) => {
		switch (status) {
			case ProjectTimeEntryStatus.Approved:
				return { backgroundColor: "#4caf50", color: "#fff" };
			case ProjectTimeEntryStatus.Pending:
				return { backgroundColor: "#f9a825", color: "#000" };
			case ProjectTimeEntryStatus.Rejected:
				return { backgroundColor: "#f44336", color: "#000" };
			case ProjectTimeEntryStatus.Open:
			default:
				return { backgroundColor: "lightblue", color: "#000" };
		}
	};

	// Status Label
	const getStatusLabel = (status: ProjectTimeEntryStatus) => {
		switch (status) {
			case ProjectTimeEntryStatus.Approved:
				return t("timetracking.status.approved");
			case ProjectTimeEntryStatus.Pending:
				return t("timetracking.status.pending");
			case ProjectTimeEntryStatus.Rejected:
				return t("timetracking.status.rejected");
			default:
				return t("timetracking.status.open");
		}
	};

	const editable = !disabled && entry.status === ProjectTimeEntryStatus.Open;
	const hasMultiplePositions = projectPositions.length > 1;

	return (
		<Stack spacing={1} sx={{ width: "100%" }}>
			{/* Row 1: Metadata — Status, Time, Position, Actions */}
			<Stack direction="row" spacing={1.5} alignItems="flex-end">
				<Chip
					label={getStatusLabel(entry.status)}
					size="small"
					sx={{ alignSelf: "center", ...getStatusStyle(entry.status) }}
				/>
				<TimeRangeInput
					value={timeRangeValue}
					onChange={(newTimeRange) => {
						const date = entry.start.split(" ")[0];
						const { start, end } = timeRangeValueToString(date, newTimeRange);
						onChange({ ...entry, start, end });
					}}
					minuteStep={timeTrackingResolution}
					readOnly={!editable}
				/>
				{hasMultiplePositions && (
					<FormControl size="small" disabled={disabled} sx={{ minWidth: 200 }}>
						<InputLabel>{t("project.position")}</InputLabel>
						<Select
							disabled={!editable}
							value={entry.projectPositionId}
							label={t("project.position")}
							onChange={(e) => onChange({ ...entry, projectPositionId: e.target.value })}>
							{projectPositions.map((pos) => (
								<MenuItem key={pos.projectPositionId} value={pos.projectPositionId}>
									{pos.saleableServiceName}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				)}
				<Stack direction="row" spacing={0.5} alignItems="center" sx={{ flex: 1, justifyContent: "flex-end" }}>
					<LogsCell
						entry={entry}
						refreshData={(entry: ProjectTimeEntry | NewTimeEntry) => onChange(entry)}
					/>
					<HistoryCell entry={entry} />
					<IconButton
						onClick={onDelete}
						disabled={!editable}
						color="error"
						size="small"
						aria-label={t("delete")}>
						<Delete />
					</IconButton>
				</Stack>
			</Stack>

			{/* Row 2: Description — full width, prominent */}
			<TextField
				label={t("description")}
				value={entry.description || ""}
				onChange={(e) => onChange({ ...entry, description: e.target.value })}
				multiline
				minRows={3}
				size="small"
				disabled={!editable}
				fullWidth
			/>
		</Stack>
	);
};
